<template>
  <div class="componentRoot">
    <ButtonRowVue>
      <template v-slot:leftSide>
        <v-col :cols="searchBarCols" class="pr-2">
          <SearchBarVue
            :labelArray="['basics.name', 'basics.city']"
            @search="updateSearchQueue"
          />
        </v-col>
      </template>
      <v-col cols="auto" v-if="$checkRouterPermission('POST')">
        <v-tooltip :text="$t('basics.new', { type: $t('basics.premise') })">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              @click="showEditPremise($event)"
              class="primaryButton squareButton"
              flat
              ><v-icon size="x-large">mdi-plus</v-icon></v-btn
            >
          </template>
        </v-tooltip>
      </v-col>
    </ButtonRowVue>

    <TableVue
      :numPages="numPages"
      @switchPage="switchPage"
      id="premiseTable"
      :resetPage="resetPage"
      :total="totalCount"
    >
      <thead>
        <tr>
          <th
            class="text-left"
            @click="switchSortBy('name')"
            :style="{
              color: sortBy == 'name' ? 'rgb(var(--v-theme-primary))' : 'unset',
            }"
          >
            <div style="display: flex; align-items: center">
              <b style="font-size: medium">{{ $t("basics.name") }}</b>

              <div v-if="sortBy == 'name'">
                <v-icon v-if="!sortDirection">mdi-menu-up</v-icon>
                <v-icon v-else>mdi-menu-down</v-icon>
              </div>
              <div v-else>
                <v-icon>mdi-menu-right</v-icon>
              </div>
            </div>
          </th>

          <th>
            <b style="font-size: medium">{{ $t("basics.address") }}</b>
          </th>
          <th
            v-if="!isMobile"
            class="text-left"
            @click="switchSortBy('lastUpdated')"
            :style="{
              color: sortBy == 'lastUpdated' ? 'rgb(var(--v-theme-primary))' : 'unset',
            }"
          >
            <div style="display: flex; align-items: center">
              <b style="font-size: medium">{{ $t("basics.lastUpdated") }}</b>

              <div v-if="sortBy == 'lastUpdated'">
                <v-icon v-if="!sortDirection">mdi-menu-up</v-icon>
                <v-icon v-else>mdi-menu-down</v-icon>
              </div>
              <div v-else>
                <v-icon>mdi-menu-right</v-icon>
              </div>
            </div>
          </th>
          <th></th>
        </tr>
      </thead>
      <tbody v-if="premises && premises.length > 0">
        <tr v-for="pr in premises" :key="pr.id" @click="showEditPremise($event, pr)">
          <td>
            {{ pr.name }}
          </td>
          <td>
            <span v-if="!pr.address">
              {{ pr.address_old }}
            </span>
            <span v-else>
              {{ pr.address.city.zip_code }} {{ pr.address.city.name }},
              {{ pr.address.street }} {{ pr.address.housing_number }}
            </span>
          </td>
          <td v-if="!isMobile">{{ getReadableDatetime(pr.last_updated) }}</td>
          <td class="text-right">
            <span @click="showEditPremise($event, pr)">
              <v-tooltip :text="$t('basics.edit', { type: $t('basics.premise') })">
                <template v-slot:activator="{ props }">
                  <v-icon v-bind="props">mdi-pencil</v-icon>
                </template>
              </v-tooltip>
            </span>
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <tr>
          <td colspan="400">
            <i>{{ $t("messages.noItems") }}</i>
          </td>
        </tr>
      </tbody>
    </TableVue>

    <PremiseEditVue
      :refreshDisplay="editPremiseDrawer"
      :premiseData="activePremise"
      @close="hideEditPremise"
      :readonly="!$checkRouterPermission('POST') && !$checkRouterPermission('PATCH')"
    />

    <LoadingScreenVue :loading="loading" />
  </div>
</template>

<script>
import PremiseEditVue from "../../../components/settings/PremiseEdit.vue";

export default {
  name: "premiseList",
  data() {
    return {
      premises: [],
      loading: false,

      numPages: undefined,
      currPage: 1,
      totalCount: undefined,
      resetPage: 0,

      sortBy: "name",
      sortDirection: true,
      searchQueue: undefined,

      activePremise: undefined,
      editPremiseDrawer: 0,
    };
  },

  computed: {
    isMobile() {
      switch (this.$vuetify.display.name) {
        case "xs":
          return true;
        case "sm":
          return true;
        case "md":
          return false;
        case "lg":
          return false;
        case "xl":
          return false;
        case "xxl":
          return false;
        default:
          return false;
      }
    },
    searchBarCols() {
      switch (this.$vuetify.display.name) {
        case "xs":
          return 9;
        case "sm":
          return 9;
        case "md":
          return 6;
        case "lg":
          return 4;
        case "xl":
          return 4;
        case "xxl":
          return 3;
        default:
          return 4;
      }
    },
  },

  components: {
    PremiseEditVue,
  },

  methods: {
    fetchData() {
      this.loading = true;

      let searchParams =
        "?page=" +
        this.currPage +
        "&order=" +
        this.sortBy +
        "&direction=" +
        (this.sortDirection ? "up" : "down");

      if (this.searchQueue) {
        searchParams += "&query=" + this.searchQueue;
      }

      this.axios
        .get(this.axios.defaults.baseURL + "localization/premises/" + searchParams)
        .then((resp) => {
          this.premises = resp.data.results;
          this.totalCount = resp.data.count;
          this.loading = false;

          if (resp.data.results.length == 0) {
            this.numPages = 0;
          } else if (this.currPage == 1) {
            this.numPages = Math.ceil(resp.data.count / resp.data.results.length);
          }
        })
        .catch((e) => {
          console.log(e);
          window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
          this.loading = false;
        });
    },

    updateSearchQueue(value) {
      this.resetPage++;
      this.currPage = 1;
      this.searchQueue = value;

      this.fetchData();
    },

    switchSortBy(val) {
      if (this.sortBy != val) {
        this.sortBy = val;
        this.sortDirection = true;
      } else {
        this.sortDirection = !this.sortDirection;
      }

      this.currPage = 1;
      this.resetPage++;

      this.fetchData();
    },

    switchPage(data) {
      this.currPage = data;

      this.fetchData();
    },
    getReadableDatetime(datetime) {
      if (datetime) return this.$moment(datetime).format("DD.MM.YYYY HH:mm");
      else return undefined;
    },
    showEditPremise(event, premise) {
      event.stopPropagation();
      event.preventDefault();

      this.activePremise = premise;
      this.editPremiseDrawer++;
    },
    hideEditPremise(data) {
      this.activePremise = undefined;
      this.editPremiseDrawer = 0;

      if (data) {
        this.fetchData();
      }
    },
  },

  mounted() {
    this.fetchData();
  },
};
</script>
